import React from 'react';

import { observer } from 'mobx-react';

import Axios from 'axios';

import { CircularProgress } from '@mui/material';

import CustomModal from 'components/CustomModal';
import { Button } from 'components/Button';

import { StyledTextField } from 'components/Input/styles';

import API from 'utils/api';

import { useStore } from 'utils/hooks/store';

import { MemberGroupUsageResponse } from 'utils/api/modules/memberGroups';

import { GROUP_USAGE_KINDS, usageCountLabel } from './usage';
import {
  DeactivateImpactBox,
  DeactivateImpactEmpty,
  DeactivateImpactHeading,
  DeactivateImpactList,
  DeactivateImpactRow,
  DeactivateModalHelpText,
  ImpactError,
} from './styles';

const CONFIRM_PHRASE = 'DEACTIVATE';

const DESCRIPTION =
  "Deactivating detaches the group from everywhere it's used, so anyone who had access or assignments through it " +
  'loses them. Its members and history are kept, and already-sent acknowledgements are unaffected. Reactivating ' +
  "brings the group back but won't restore it to where it was used.";

interface DeactivateGroupModalProps {
  open: boolean;
  onClose: () => void;
}

const DeactivateGroupModal: React.FunctionComponent<DeactivateGroupModalProps> = observer(({ open, onClose }) => {
  const { memberGroupDetails } = useStore();
  const groupUuid = memberGroupDetails.group?.uuid;
  const [usage, setUsage] = React.useState<MemberGroupUsageResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [confirm, setConfirm] = React.useState('');

  // The modal is always mounted, so the !open guard is what makes the fetch lazy.
  React.useEffect(() => {
    if (!open || !groupUuid) {
      return undefined;
    }
    // Closing aborts the request rather than just ignoring its result, so an open/close/reopen
    // can't leave the previous fetch in flight and resolve into the reopened modal.
    const cancelTokenSource = Axios.CancelToken.source();
    setLoading(true);
    setError(false);

    API.memberGroups
      .loadMemberGroupUsage(groupUuid, cancelTokenSource)()
      .then((response) => {
        setUsage(response.data);
        setLoading(false);
      })
      .catch((requestError) => {
        if (Axios.isCancel(requestError)) {
          return;
        }
        setError(true);
        setLoading(false);
      });

    return () => cancelTokenSource.cancel();
  }, [open, groupUuid]);

  // Counts are cleared on close so reopening can't paint the previous fetch's numbers.
  const handleClose = (): void => {
    setConfirm('');
    setUsage(null);
    setError(false);
    onClose();
  };

  const handleDeactivate = async (): Promise<void> => {
    setSaving(true);
    const ok = await memberGroupDetails.deactivate();
    setSaving(false);
    if (ok) {
      handleClose();
    }
  };

  const usedKinds = usage ? GROUP_USAGE_KINDS.filter((kind) => usage[kind] > 0) : [];
  // Deliberately not gated on `error`: a failed informational GET must not block a reversible write.
  const canDeactivate = confirm === CONFIRM_PHRASE && !saving && !loading;

  return (
    <CustomModal
      openModal={open}
      onClose={handleClose}
      modalTitle="Deactivate this group?"
      modalDescription={DESCRIPTION}
      buttonsSpacing="space-between"
      cancelButton={
        <Button variant="outlined" size="large" onClick={handleClose} data-testid="deactivate-group-cancel-button">
          Cancel
        </Button>
      }
      submitButton={
        <Button
          variant="contained"
          size="large"
          onClick={handleDeactivate}
          disabled={!canDeactivate}
          data-testid="deactivate-group-confirm-button"
        >
          Deactivate group
        </Button>
      }
    >
      <div data-testid="deactivate-group-modal">
        {loading && <CircularProgress size={18} data-testid="deactivate-group-usage" />}
        {error && !loading && (
          <ImpactError data-testid="deactivate-group-usage">Couldn’t check where this group is used.</ImpactError>
        )}
        {usage && !loading && !error && usedKinds.length > 0 && (
          <DeactivateImpactBox data-testid="deactivate-group-usage">
            <DeactivateImpactHeading>This group is currently used in:</DeactivateImpactHeading>
            <DeactivateImpactList>
              {usedKinds.map((kind) => (
                <DeactivateImpactRow key={kind} data-testid={`deactivate-group-usage-${kind}`}>
                  {usageCountLabel(kind, usage[kind])}
                </DeactivateImpactRow>
              ))}
            </DeactivateImpactList>
          </DeactivateImpactBox>
        )}
        {usage && !loading && !error && usedKinds.length === 0 && (
          <DeactivateImpactEmpty data-testid="deactivate-group-usage">
            This group isn’t used in any live documents or audits.
          </DeactivateImpactEmpty>
        )}
        <DeactivateModalHelpText>
          Type <b>{CONFIRM_PHRASE}</b> to confirm.
        </DeactivateModalHelpText>
        <StyledTextField
          variant="outlined"
          placeholder={CONFIRM_PHRASE}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          fullWidth
          inputProps={{ 'aria-label': `Type ${CONFIRM_PHRASE} to confirm` }}
          data-testid="deactivate-group-confirm-input"
        />
      </div>
    </CustomModal>
  );
});

export default DeactivateGroupModal;
